// ═══════════════════════════════════════════════════════════════
// SHELTER MODULE — Pets CRUD
// Pet tracking: species, breed, owner, shelter location, QR
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Drawer, Divider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh, IconCamera,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useNavigate } from 'react-router-dom';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { ShelterPet, PetStatus } from '../../types/shelter';
import { PET_SPECIES } from '../../types/shelter';

const STATUS_OPTIONS: { value: PetStatus; label: string }[] = [
  { value: 'sheltered', label: 'Sheltered' },
  { value: 'transferred', label: 'Transferred' },
  { value: 'discharged', label: 'Discharged' },
  { value: 'missing', label: 'Missing' },
];

const PET_STATUS_COLORS: Record<PetStatus, string> = {
  sheltered: 'var(--color-success)',
  transferred: 'var(--color-primary-700)',
  discharged: '#939598',
  missing: 'var(--color-error)',
};

const EMPTY: Partial<ShelterPet> = {
  name: '', species: '', breed: '', color: '', weight: '', ownerId: '',
  shelterId: '', qrCode: '', status: 'sheltered', medicalNeeds: '',
  locationInShelter: '', photoUrl: '', notes: '',
};

export default function Pets() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    pets, loadingPets, survivors, shelters,
    loadPets, loadSurvivors, loadShelters, createPet, updatePet, deletePet,
  } = useShelterStore();

  const navigate = useNavigate();
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelterPet | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShelterPet | null>(null);
  const [detailTarget, setDetailTarget] = useState<ShelterPet | null>(null);
  const [form, setForm] = useState<Partial<ShelterPet>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadPets(); loadSurvivors(); loadShelters(); }, [incidentId, loadPets, loadSurvivors, loadShelters]);

  const survivorMap = useMemo(() => {
    const m = new Map<string, string>();
    survivors.forEach(s => m.set(String(s.id), `${s.firstName} ${s.lastName}`.trim()));
    return m;
  }, [survivors]);

  const shelterMap = useMemo(() => {
    const m = new Map<string, string>();
    shelters.forEach(s => m.set(String(s.id), s.name));
    return m;
  }, [shelters]);

  // Store already server-side filters by active incident; use directly
  const iSurvivors = incidentId ? survivors : [];
  const iShelters = incidentId ? shelters : [];

  const survivorOptions = useMemo(
    () => iSurvivors.map(s => ({
      value: String(s.id),
      label: `${s.firstName} ${s.lastName}`.trim() || `Survivor #${s.id}`,
    })),
    [iSurvivors],
  );

  const shelterOptions = useMemo(
    () => iShelters.map(s => ({ value: String(s.id), label: s.name || `Shelter #${s.id}` })),
    [iShelters],
  );

  const iPets = incidentId ? pets : [];

  const filtered = useMemo(() => {
    let list = iPets;
    if (statusFilter && statusFilter !== 'all') list = list.filter(p => p.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.species || '').toLowerCase().includes(q) ||
        (p.breed || '').toLowerCase().includes(q) ||
        (survivorMap.get(p.ownerId) ?? '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return a.name.localeCompare(b.name);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iPets, statusFilter, search, survivorMap, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [search, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setField = (key: keyof ShelterPet, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setModalOpen(true); };
  const openEdit = (p: ShelterPet) => { setEditTarget(p); setForm({ ...p }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      notifications.show({ message: 'Pet name is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updatePet(editTarget.id, payload);
        notifications.show({ message: 'Pet updated', color: 'var(--color-success)' });
      } else {
        await createPet(payload);
        notifications.show({ message: 'Pet created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deletePet(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.name} deleted`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Pets</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingPets} onClick={() => loadPets()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Pet</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search name, species, breed, owner..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} pet{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('name')}>Name{sortIcon('name')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('species')}>Species{sortIcon('species')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Breed</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Owner</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Shelter</th>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>QR Code</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingPets ? 'Loading...' : 'No pets found'}
                </td></tr>
              )}
              {pageRows.map(p => (
                <tr key={p.id} onClick={() => setDetailTarget(p)} style={{ cursor: 'pointer', borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '8px 16px', fontWeight: 500 }}>{p.name || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>
                    {(PET_SPECIES.find(s => s.value === p.species)?.label ?? p.species) || '-'}
                  </td>
                  <td style={{ padding: '8px 16px' }}>{p.breed || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{survivorMap.get(p.ownerId) || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{shelterMap.get(p.shelterId) || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>
                    <Badge size="sm" color={PET_STATUS_COLORS[p.status] ?? '#939598'}>{p.status}</Badge>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    {p.qrCode ? <Text size="xs" ff="monospace">{p.qrCode}</Text> : '-'}
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    {canEdit && (
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(p)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(p)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Pet' : 'Add Pet'} centered size="lg">
        <Stack>
          <Text size="sm" fw={600} c="dimmed">Pet Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Name" required value={form.name ?? ''}
              onChange={e => setField('name', e.target.value)} />
            <Select label="Species" data={PET_SPECIES} value={form.species ?? ''}
              onChange={v => setField('species', v ?? '')} clearable />
            <TextInput label="Breed" value={form.breed ?? ''}
              onChange={e => setField('breed', e.target.value)} />
            <TextInput label="Color" value={form.color ?? ''}
              onChange={e => setField('color', e.target.value)} />
            <TextInput label="Weight" value={form.weight ?? ''}
              onChange={e => setField('weight', e.target.value)} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={form.status ?? 'sheltered'}
              onChange={v => setField('status', v ?? 'sheltered')} />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed" mt="sm">Assignment</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Owner" data={survivorOptions} value={form.ownerId ?? ''}
              onChange={v => setField('ownerId', v ?? '')} searchable clearable />
            <Select label="Shelter" data={shelterOptions} value={form.shelterId ?? ''}
              onChange={v => setField('shelterId', v ?? '')} searchable clearable />
            <TextInput label="Location in Shelter" value={form.locationInShelter ?? ''}
              onChange={e => setField('locationInShelter', e.target.value)} />
            <TextInput label="QR Code" value={form.qrCode ?? ''}
              onChange={e => setField('qrCode', e.target.value)} />
          </SimpleGrid>

          <Textarea label="Medical Needs" value={form.medicalNeeds ?? ''}
            onChange={e => setField('medicalNeeds', e.target.value)} autosize minRows={2} />
          <div>
            <Text size="sm" fw={500} mb={4}>Photo</Text>
            {form.photoUrl && (
              <img src={form.photoUrl} alt="Pet" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 4, marginBottom: 8 }} />
            )}
            <Button
              variant="light"
              size="xs"
              leftSection={<IconCamera size={14} />}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                // Use camera on mobile devices
                if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
                  input.capture = 'environment';
                }
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setField('photoUrl', reader.result as string);
                  reader.readAsDataURL(file);
                };
                input.click();
              }}
            >
              {form.photoUrl ? 'Change Photo' : (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Take Photo' : 'Upload Photo')}
            </Button>
          </div>
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Pet'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Pet" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)} title="Pet Details"
        position="right" size="md">
        {detailTarget && (
          <Stack>
            <Group>
              <Text size="xl" fw={700}>{detailTarget.name || 'Unnamed Pet'}</Text>
              <Badge color="var(--data-viz-650)">{(PET_SPECIES.find(s => s.value === detailTarget.species)?.label ?? detailTarget.species) || 'Unknown'}</Badge>
              <Badge color={PET_STATUS_COLORS[detailTarget.status] ?? '#939598'}>{detailTarget.status}</Badge>
            </Group>

            <Divider />

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" c="dimmed">Breed</Text>
                <Text size="sm">{detailTarget.breed || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Color</Text>
                <Text size="sm">{detailTarget.color || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Weight</Text>
                <Text size="sm">{detailTarget.weight || '-'}</Text>
              </div>
            </SimpleGrid>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Owner</Text>
              {detailTarget.ownerId && survivorMap.get(detailTarget.ownerId) ? (
                <Text size="sm" c="var(--color-primary-700)" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => { setDetailTarget(null); navigate('/shelter/survivor/' + detailTarget.ownerId); }}>
                  {survivorMap.get(detailTarget.ownerId)} — View Owner
                </Text>
              ) : (
                <Text size="sm">-</Text>
              )}
            </div>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" c="dimmed">Shelter</Text>
                <Text size="sm">{shelterMap.get(detailTarget.shelterId) || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Location in Shelter</Text>
                <Text size="sm">{detailTarget.locationInShelter || '-'}</Text>
              </div>
            </SimpleGrid>

            <div>
              <Text size="xs" c="dimmed">QR Code</Text>
              <Text size="sm" ff="monospace">{detailTarget.qrCode || '-'}</Text>
            </div>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Medical Needs</Text>
              <Text size="sm">{detailTarget.medicalNeeds || '-'}</Text>
            </div>

            <div>
              <Text size="xs" c="dimmed">Notes</Text>
              <Text size="sm">{detailTarget.notes || '-'}</Text>
            </div>

            <Divider />

            {canEdit && (
            <Group>
              <Button size="xs" leftSection={<IconPencil size={14} />}
                onClick={() => { const p = detailTarget; setDetailTarget(null); openEdit(p); }}>
                Edit
              </Button>
              <Button size="xs" color="var(--color-error)" leftSection={<IconTrash size={14} />}
                onClick={() => { const p = detailTarget; setDetailTarget(null); setDeleteTarget(p); }}>
                Delete
              </Button>
            </Group>
            )}
          </Stack>
        )}
      </Drawer>
    </div>
  );
}
